import { useMutation } from "@tanstack/react-query";
import { useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import type { UnlockConfirm } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import FormTitle from "~/components/login/formTitle/FormTitle";
import NotificationBlock from "~/components/notificationBlock/NotificationBlock";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useAuth } from "~/contexts/AuthProvider";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./Login.module.scss";

const UnlockPage = () => {
  usePageTitle(PAGE_TITLES.unlockAccount);

  const [searchParams] = useSearchParams();
  // Snapshot the unlock token at mount so the URL can be safely cleaned up
  // below. Any subsequent navigation away from `?token=...` must not lose
  // the value we still need to send to the server.
  const [unlockToken] = useState(() => searchParams.get("token"));
  const navigate = useNavigate();
  const { token, user, setSession } = useAuth();

  // Strip `?token=` from the URL before the browser paints and before any
  // analytics SDK's route-level `useEffect` (Sentry navigation transactions,
  // Segment/Intercom/GA page-views) gets a chance to read `window.location`.
  // `useLayoutEffect` runs synchronously after commit, before paint, and
  // before any `useEffect` anywhere in the tree — so route-mounted SDKs
  // never see the token. The initial SDK pageload-init may have already
  // captured the URL on first navigation into the app (browser-level
  // page-load tracing), which is unavoidable on the client; everything that
  // fires per-route after mount is covered.
  useLayoutEffect(() => {
    if (searchParams.has("token")) {
      navigate(routes.unlock(), { replace: true });
    }
    // Intentionally runs once on mount: subsequent param changes are
    // either us replacing it away (no-op) or external — neither warrants
    // re-running this side effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No rendered fields — useForm is here only to plug into useFormErrors,
  // so any `unlock_token` validation from the server is surfaced in the
  // base-errors block (same pattern as CreateNewPasswordPage).
  const { setError } = useForm<UnlockConfirm>();
  const { baseErrors, applyServerErrors } = useFormErrors(setError, [
    "unlock_token",
  ]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: UnlockConfirm) => apiClient.unlock.confirmCreate(data),
  });

  if (!unlockToken) {
    return (
      <>
        <FormTitle title="Invalid Link" />
        <div className={styles.infoText}>
          This unlock link is invalid or expired.
        </div>
        <div className={styles.footer}>
          <Link className={styles.footerLink} to={routes.auth.login()}>
            Back to login
          </Link>
        </div>
      </>
    );
  }

  const onUnlock = async () => {
    const result = await tryCatchParsed(
      mutateAsync({ unlock_token: unlockToken }),
    );

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    setSession(result.data.data.access_token);
    // No guard wraps /unlock (a locked user with a stale token must still be
    // able to reach the page), so the success redirect is explicit here.
    // AppGate's MFA gate will still take over if the new session requires it.
    // `replace` drops the tokenless /unlock entry from history — Back would
    // otherwise land on the invalid-link screen.
    navigate(routes.home(), { replace: true });
  };

  return (
    <>
      <FormTitle title="Unlock Your Account" />
      <div className={styles.infoText}>
        Your account was temporarily locked due to multiple failed sign-in
        attempts. Click the button below to unlock and sign in.
      </div>
      {token && (
        <NotificationBlock type="warning">
          {user?.email
            ? `You are currently signed in as ${user.email}. Continuing will sign you out of that account.`
            : "You are currently signed in. Continuing will sign you out of that account."}
        </NotificationBlock>
      )}
      <FormBaseErrors errors={baseErrors} />
      <Button
        fullWidth
        onClick={onUnlock}
        disabled={isPending}
        withSpinner={isPending}
      >
        Unlock account
      </Button>
    </>
  );
};

export default UnlockPage;
